import type { NavItems } from '@ksyos/design-system';
import { Header, KsyosLogo, TopNav } from '@ksyos/design-system';
import React, { useCallback, useMemo } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import type { IOrganization } from '../../../interfaces/';
import type { EmploymentSwitch } from '../types/employmentSwitch';
import type { GlobalUserContext } from '../types/globalUserContext';
import type { UserEmployment } from '../types/userEmployment';
import { EmploymentSwitchModal } from './EmploymentSwitchModal';
import { UserNavigation } from './UserNavigation';

export type DesktopHeaderProps = {
    currentOrganization: Pick<IOrganization, 'id' | 'name' | 'suppressEmployeePayoutOverview'>;
    employments: UserEmployment[];
    employmentSwitch: EmploymentSwitch | null;
    frontendSettings: { environment: string };
    globalUserContext: GlobalUserContext;
    navigationItems: NavItems[];
    paymentsUrl: string;
    profileUrl: string;
    setEmploymentSwitch: React.Dispatch<React.SetStateAction<EmploymentSwitch | null>>;
    userInformation: Partial<GlobalUserContext>;
};

export function DesktopHeader({
    navigationItems,
    currentOrganization,
    employments,
    globalUserContext,
    paymentsUrl,
    profileUrl,
    setEmploymentSwitch,
    userInformation,
    frontendSettings,
    employmentSwitch,
}: Readonly<DesktopHeaderProps>) {
    const renderItems = useCallback((menuItem: NavItems) => {
        if (menuItem.items) {
            return (
                <TopNav.Menu
                    icon={menuItem.icon}
                    key={menuItem.key}
                    label={menuItem.label}
                >
                    {menuItem.items.map((subItem) => renderItems(subItem))}
                </TopNav.Menu>
            );
        }
        const { key, ...rest } = menuItem;

        return (
            <TopNav.Link
                key={key}
                {...rest}
            />
        );
    }, []);

    const renderedNavigationItems = useMemo(() => {
        return navigationItems.map((item) => renderItems(item));
    }, [navigationItems, renderItems]);

    return (
        <Header>
            <Header.Item sx={{ marginRight: '32px' }}>
                <KsyosLogo
                    height="30px"
                    width="99px"
                />
            </Header.Item>
            <TopNav>{renderedNavigationItems}</TopNav>
            {frontendSettings.environment !== 'production' && (
                <Header.Item>
                    <Header.EnvLabel>{frontendSettings.environment} omgeving</Header.EnvLabel>
                </Header.Item>
            )}
            <Header.Item className={RemoveScroll.classNames.fullWidth}>
                <UserNavigation
                    currentOrganization={currentOrganization}
                    employments={employments}
                    globalUserContext={globalUserContext}
                    paymentsUrl={paymentsUrl}
                    profileUrl={profileUrl}
                    setEmploymentSwitch={setEmploymentSwitch}
                    userInformation={userInformation}
                />
            </Header.Item>
            <EmploymentSwitchModal
                currentOrganization={currentOrganization}
                employmentSwitch={employmentSwitch}
                setEmploymentSwitch={setEmploymentSwitch}
            />
        </Header>
    );
}
